import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useGetCurrentUserQuery, useSignOutMutation } from 'ev-api/core';
import { URL_PREFIX } from 'ev-config/config';
import { SessionStorage } from 'ev-utils/storage';

import { signOutRedirect } from './signOut';

// Accountless patients only exist for the duration of a Digital Front Door /
// quick-entry / callback visit (CU-86b7369yn). Everywhere else in the patient
// app funnels an invalid-profile patient into registration, which turns that
// throwaway session into an onboarding flow it was never meant to reach —
// including the bare /ui root, which useActiveSessionRedirect sends patients
// to /patient/home. Guard exactly those two entry points rather than every
// route, since both already fetch current_user today and this only adds a
// check on top of that existing call.
const isGuardedPath = (pathname: string) =>
  pathname === URL_PREFIX ||
  pathname === `${URL_PREFIX}/` ||
  pathname.startsWith(`${URL_PREFIX}/patient`);

export const useAccountlessRouteGuard = (skip = false) => {
  const location = useLocation();
  const isGuarded = !skip && isGuardedPath(location.pathname);
  const [signOut] = useSignOutMutation();
  const hasTriggered = useRef(false);

  const {
    data: currentUser,
    isFetching,
    isUninitialized,
  } = useGetCurrentUserQuery(undefined, {
    skip: !isGuarded,
  });

  const isAccountless = !!currentUser?.attributes.accountless;

  useEffect(() => {
    if (!isGuarded || isFetching || hasTriggered.current || !isAccountless) {
      return;
    }

    hasTriggered.current = true;
    signOut()
      .then(() => {
        SessionStorage.removeItem('_eVisitSession');
      })
      .catch(e => console.error(e))
      .finally(() => {
        void signOutRedirect(null);
      });
  }, [isAccountless, isFetching, isGuarded, signOut]);

  // Callers must hold off rendering guarded routes while this is true.
  // Otherwise a page like PatientHome can act on the same current_user
  // result (e.g. redirecting an invalid-profile patient to registration)
  // before the async sign-out above has a chance to win the race.
  // Once accountless is confirmed we stay "checking" forever — there's
  // nothing safe to render, we're just waiting on signOutRedirect to
  // navigate away.
  const isChecking =
    isGuarded && (isUninitialized || isFetching || isAccountless);

  return { isChecking };
};
